import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { quotesService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  FileText,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Download,
  ArrowRightLeft,
  ExternalLink,
} from 'lucide-react';

export const QuoteList = () => {
  const [quotes, setQuotes] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quotesService.list({ search, page, limit: 15 });
      if (res && res.data) {
        if (res.data.data) {
          setQuotes(res.data.data);
          setPagination({
            currentPage: res.data.current_page,
            lastPage: res.data.last_page,
            total: res.data.total,
            from: res.data.from,
            to: res.data.to,
          });
        } else {
          setQuotes(Array.isArray(res.data) ? res.data : []);
        }
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar cotizaciones.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotes]);

  const handleConvert = async (quote) => {
    setConvertingId(quote.id);
    try {
      const res = await quotesService.convert(quote.id);
      success('Cotización convertida en Factura exitosamente.');
      if (res && res.data && res.data.invoice_id) {
        navigate(`/invoices/${res.data.invoice_id}`);
      } else {
        fetchQuotes();
      }
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al convertir cotización.');
    } finally {
      setConvertingId(null);
    }
  };

  const handleDelete = async () => {
    if (!quoteToDelete) return;
    setSubmitting(true);
    try {
      await quotesService.delete(quoteToDelete.id);
      success('Cotización eliminada correctamente.');
      setDeleteConfirmOpen(false);
      setQuoteToDelete(null);
      fetchQuotes();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar cotización.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async (quote) => {
    try {
      await quotesService.downloadPdf(quote.id, quote.number);
      success('PDF de cotización descargado.');
    } catch (err) {
      toastError('No se pudo descargar el archivo PDF.');
    }
  };

  const handleOpenPdf = (quote) => {
    window.open(api.getPdfUrl('quotes', quote.id), '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Cotizaciones & Presupuestos
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Genera presupuestos para tus clientes y conviértelos a facturas con un solo clic
          </p>
        </div>

        <Link to="/quotes/create">
          <Button icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
            Nueva Cotización
          </Button>
        </Link>
      </div>

      {/* List Card */}
      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por cliente o número..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando cotizaciones..." className="py-12" />
          ) : quotes.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No hay cotizaciones emitidas"
              description={search ? "No se encontraron cotizaciones para tu búsqueda." : "Crea tu primera cotización profesional para tus clientes."}
              actionLabel="Crear Cotización"
              onAction={() => navigate('/quotes/create')}
              actionIcon={Plus}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Válida Hasta</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {quotes.map((q) => {
                    const isConverted = q.status === 1;
                    return (
                      <tr
                        key={q.id}
                        className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                      >
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                          #{q.number || String(q.id).padStart(6, '0')}
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900 dark:text-slate-100">
                            {q.customer?.full_name || 'Cliente sin asignar'}
                          </div>
                          {q.customer?.email && (
                            <div className="text-[11px] text-slate-400">{q.customer.email}</div>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                          {q.quote_date || '—'}
                        </td>

                        <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                          {q.valid_until || '—'}
                        </td>

                        <td className="py-3.5 px-4 font-black text-sm text-slate-900 dark:text-slate-100">
                          {q.currency?.symbol || '$'} {Number(q.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          {isConverted ? (
                            <Badge variant="success" size="sm" dot>Convertida</Badge>
                          ) : (
                            <Badge variant="primary" size="sm" dot>Borrador / Abierta</Badge>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/quotes/${q.id}`}>
                              <Button variant="ghost" size="sm" icon={Eye} title="Ver Detalle" />
                            </Link>

                            {!isConverted && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={ArrowRightLeft}
                                isLoading={convertingId === q.id}
                                onClick={() => handleConvert(q)}
                                title="Convertir a Factura"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                              >
                                Facturar
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={ExternalLink}
                              onClick={() => handleOpenPdf(q)}
                              title="Ver PDF en navegador"
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Download}
                              onClick={() => handleDownloadPdf(q)}
                              title="Descargar PDF"
                            />

                            {!isConverted && (
                              <Link to={`/quotes/edit/${q.id}`}>
                                <Button variant="ghost" size="sm" icon={Edit2} title="Editar" />
                              </Link>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Trash2}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                              onClick={() => {
                                setQuoteToDelete(q);
                                setDeleteConfirmOpen(true);
                              }}
                              title="Eliminar"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar cotización?"
        message={`¿Estás seguro de que deseas eliminar la cotización #${quoteToDelete?.number}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar Cotización"
        isLoading={submitting}
      />
    </div>
  );
};
